/**
 * ApprovalChain — the decision trail from "Leave Apply Flow" 5a, screen 3.
 * @file src/web/features/leave/components/ApprovalChain.tsx
 *
 * @llm-rule WHEN: Showing one leave request's progress to the person who filed it
 * @llm-rule NOTE: Answers "where has this got to, and who is sitting on it?"
 *   without anyone having to ask. The "everyone above them" step shows only
 *   while PENDING — once a decision exists the decision is the point, and that
 *   line becomes noise under a settled answer.
 */

import type { ReactNode } from 'react';
import { ROLE_LABEL, type OrgRole } from '../../../shared/domain';
import { LEAVE_STATUS_TONE, type LeaveRequest } from '../leave.types';

function when(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function Step({
  state,
  title,
  detail,
  last,
}: {
  /** done = filled dark, active = filled brand, waiting = dashed outline. */
  state: 'done' | 'active' | 'waiting';
  title: ReactNode;
  detail: ReactNode;
  last?: boolean;
}) {
  const dot =
    state === 'done'
      ? { background: 'var(--ink)', border: '2px solid var(--ink)' }
      : state === 'active'
        ? { background: 'var(--brand)', border: '2px solid var(--brand)' }
        : { background: 'transparent', border: '2px dashed var(--ink-ghost)' };

  return (
    <div className="flex gap-3">
      <div className="flex w-4 flex-none flex-col items-center">
        <span className="size-4 flex-none rounded-full" style={dot} />
        {!last && (
          <span
            className="w-0.5 flex-1"
            style={{ background: state === 'done' ? 'var(--ink)' : 'var(--rule)' }}
          />
        )}
      </div>

      <div className={`flex flex-col gap-0.5 ${last ? '' : 'pb-4'}`}>
        <span
          className="text-[13.5px] font-medium"
          style={{ color: state === 'waiting' ? 'var(--ink-faint)' : 'var(--ink)' }}
        >
          {title}
        </span>
        <span className="text-[11.5px] text-ink-faint">{detail}</span>
      </div>
    </div>
  );
}

export function ApprovalChain({
  row,
  approverLabel,
}: {
  row: LeaveRequest;
  /** Who decides at this company — "your CTO or CEO". */
  approverLabel: string;
}) {
  const decided = row.status === 'APPROVED' || row.status === 'REJECTED';
  const tone = LEAVE_STATUS_TONE[row.status];

  return (
    <div className="flex flex-col gap-3">
      {/* Summary card */}
      <div className="rounded-[13px] border-2 border-ink bg-paper p-3.5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-[15px] font-semibold text-ink">
            {row.days} day{row.days === 1 ? '' : 's'} · {row.dayPart === 'FULL_DAY' ? 'Full day' : 'Half day'}
          </span>
          <span
            className="label-mono rounded-md px-2 py-0.5 text-[10px]"
            style={{ background: tone.bg, color: tone.fg }}
          >
            {row.status}
          </span>
        </div>

        <p className="mt-1.5 text-[13px] leading-relaxed text-ink-soft">Reason: {row.reason}</p>
        <p className="mt-1 text-[11.5px] text-ink-ghost">Submitted {when(row.createdAt)}</p>
      </div>

      <div>
        <div className="label-mono pb-2.5 text-ink-faint">Approval chain</div>

        <Step
          state="done"
          title="Submitted by you"
          detail={when(row.createdAt)}
        />

        <Step
          // Once decided this is the end of the chain, so it closes the line.
          last={decided}
          state={decided ? 'done' : 'active'}
          title={
            row.decidedBy
              ? `${row.decidedBy.name} · ${ROLE_LABEL[row.decidedBy.orgRole as OrgRole]}`
              : approverLabel
          }
          detail={
            decided ? (
              <span style={{ color: tone.fg }}>
                {row.status === 'APPROVED' ? 'Approved' : 'Rejected'} {when(row.decidedAt)}
                {row.decisionNote ? ` — “${row.decisionNote}”` : ''}
              </span>
            ) : (
              <span style={{ color: 'var(--brand)' }}>Reviewing — waiting on a decision</span>
            )
          }
        />

        {/*
          Only worth saying while the answer is still coming. Once it is
          approved or rejected the outcome is the point, and "everyone above
          them sees it" is just noise under a settled decision.
        */}
        {!decided && (
          <Step
            state="waiting"
            title="Everyone above them"
            detail="Sees the decision on their Leave screen"
            last
          />
        )}
      </div>
    </div>
  );
}
